import { useState } from 'react';
import { ApiError, webhookSettingsApi, type WebhookSettingsView } from '../../api/client';
import { Button, Card, Chip, Field, LoadError, Loading, Notice } from '../../components/ui';
import { shortWhen } from '../../lib/format';
import { useData } from '../../lib/useData';

/**
 * Stitch Admin → System → Webhooks (1.12.19): where companies' webhook addresses point
 * (https://<base>/msg/channels/<id>), how to set the domain up in EasyPanel, and whether a webhooks
 * container of its own (STITCH_SURFACE=webhooks) has been seen.
 */
export function WebhooksCard() {
  const data = useData(() => webhookSettingsApi.get(), []);
  const [base, setBase] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null);
  const v: WebhookSettingsView | undefined = data.data;
  const save = async (value: string | null) => {
    setBusy(true);
    setNote(null);
    try {
      await webhookSettingsApi.save(value);
      setBase(null);
      data.reload();
      setNote({ tone: 'ok', text: 'Saved. New addresses use it at once; every address already given out moves with it.' });
    } catch (err) {
      setNote({ tone: 'crit', text: err instanceof ApiError || err instanceof Error ? err.message : 'That didn’t work.' });
    } finally {
      setBusy(false);
    }
  };
  const seen = v?.container;
  return (
    <Card title="Webhooks" action={v ? <Chip small tone={seen?.startedAt ? 'ok' : 'neutral'}>{seen?.startedAt ? 'Own container' : 'In the portal'}</Chip> : undefined}>
      <div className="card-body settings-section">
        <p className="muted settings-lede">
          Outside services post into companies’ channels at <span className="mono">{v ? `${v.url}/msg/channels/…` : '…/msg/channels/…'}</span>. On that domain only webhook addresses answer: never the
          portal, its sign-in or its API.
        </p>
        {data.error ? (
          <LoadError error={data.error} retry={data.reload} />
        ) : !v ? (
          <Loading label="Loading webhooks" />
        ) : (
          <>
            <dl className="facts">
              <dt>Base address</dt>
              <dd>
                <span className="mono">{v.url}</span>
                <small className="muted" style={{ display: 'block' }}>
                  {v.source === 'stitch' ? 'Saved in Stitch' : v.source === 'easypanel' ? 'From EasyPanel (WEBHOOK_BASE_URL)' : 'Stitch’s default'}
                </small>
              </dd>
              <dt>Webhooks container</dt>
              <dd>
                {seen?.startedAt ? (
                  <>
                    Started {shortWhen(seen.startedAt)}
                    {seen.version ? ` (${seen.version}${seen.build ? `, ${seen.build}` : ''})` : ''}
                    <small className="muted" style={{ display: 'block' }}>{seen.lastMessageAt ? `Last message ${shortWhen(seen.lastMessageAt)}` : 'No messages through it yet'}</small>
                  </>
                ) : (
                  <>
                    Not seen yet
                    <small className="muted" style={{ display: 'block' }}>
                      Until one runs, the portal’s containers answer {v.host} too.
                    </small>
                  </>
                )}
              </dd>
            </dl>
            <details className="settings-help">
              <summary>Setting up {v.host}</summary>
              <ol>
                <li>
                  In the EasyPanel project Stitch is in: <strong>+ Service → App</strong>, named <span className="mono">webhooks</span>, from the same zip as Stitch.
                </li>
                <li>
                  Environment: the portal’s <span className="mono">DATABASE_URL</span>, <span className="mono">DATA_ENCRYPTION_KEY</span> and <span className="mono">REDIS_URL</span>, plus{' '}
                  <span className="mono">STITCH_SURFACE=webhooks</span> and <span className="mono">TRUST_PROXY=1</span>.
                </li>
                <li>
                  Domains: add <span className="mono">{v.host}</span> (HTTPS) to that app, port 4000. Point the domain’s DNS (an A record) at the server.
                </li>
                <li>Deploy. This card says when it started. Until then, add the domain to the Stitch app instead: it answers only webhook addresses there.</li>
              </ol>
            </details>
            <div className="settings-row">
              <Field
                label="Base address"
                value={base ?? (v.source === 'stitch' ? v.url : '')}
                placeholder={v.env ?? 'https://webhooks.stitchuc.team'}
                onChange={(e) => setBase(e.target.value)}
                hint="An https address of its own (not the portal’s), with no path."
              />
            </div>
            {note && <Notice tone={note.tone}>{note.text}</Notice>}
            <div className="settings-actions">
              <Button variant="primary" disabled={busy || base === null || !base.trim()} onClick={() => void save(base!.trim())}>
                {busy ? 'Saving…' : 'Save'}
              </Button>
              {v.source === 'stitch' && (
                <Button variant="ghost" disabled={busy} onClick={() => void save(null)}>
                  Back to {v.env ? 'EasyPanel’s' : 'the default'}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
